import { useEffect, useRef } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { ChatList } from '../chatlist/ChatList'
import { RueckfrageHost } from '../../components/Rueckfrage'
import { repariereAbo } from '../../lib/push'
import { merkeOrt } from '../../lib/letzterChat'
import { abonniereTippziel } from '../../lib/tippziel'
import { useMe, useSettings } from '../../hooks/queries'
import { blasenVariablen } from '../../lib/blasenfarben'
import { vomKonto } from '../../lib/darstellung'
import { knopfVariablen } from '../../lib/knopffarbe'
import { NADELFARBEN } from '../map/nadelfarben'

/**
 * Zweispaltig am Rechner, einspaltig am Telefon.
 *
 * Welche Spalte auf dem Telefon zu sehen ist, entscheidet der Pfad: die
 * Startseite zeigt die Chatliste, jeder tiefere Pfad den geöffneten Inhalt.
 * Das ist bewusst nicht in einem Zustand gespeichert -- so bringt der
 * Zurück-Knopf des Browsers einen von selbst wieder zur Liste.
 */
export function Shell() {
  const location = useLocation()
  const navigate = useNavigate()
  const pane = location.pathname === '/' ? 'list' : 'thread'

  /**
   * Ein Tipp auf eine Benachrichtigung führt in ihren Chat.
   *
   * **Hier und nicht in `main.tsx`**, weil hier der Router steht: der Sprung
   * soll die Seite nicht neu laden. Zugehört wird schon vorher (siehe
   * `lib/tippziel.ts`) — was in der Zwischenzeit ankam, liefert das Abonnement
   * beim Anmelden nach.
   */
  useEffect(() => abonniereTippziel((pfad) => navigate(pfad)), [navigate])

  // Einmal je Start: hat dieses Gerät seine Anmeldung für Benachrichtigungen
  // noch? Sie kann ohne Zutun verschwinden, und dann kommt einfach nichts
  // mehr -- ohne dass irgendwo etwas danach aussieht. Stillschweigend
  // wiederherstellen ist hier richtig: gefragt wurde schon, die Erlaubnis
  // liegt vor. Geht es nicht, bleibt es dabei; der Hinweis steht dann unter
  // Einstellungen.
  useEffect(() => {
    void repariereAbo().catch(() => undefined)
  }, [])

  /**
   * Die Schriftgröße vom Konto — sobald sie da ist.
   *
   * **Hier und nicht auf der Einstellungsseite**, weil sie überall gilt und
   * nicht nur dort, wo man sie wählt. Beim Start steht schon die Kopie aus
   * dem Browser am Wurzelelement; das hier ist der Abgleich, wenn jemand sie
   * an einem anderen Gerät verstellt hat. `vomKonto` tut nichts, wenn beide
   * dasselbe sagen — sonst schriebe jeder Durchgang in den Speicher.
   */
  const schrift = useMe().data?.font_scale
  useEffect(() => {
    vomKonto(schrift)
  }, [schrift])

  /**
   * Die eingestellten Farben der Sprechblasen — am Wurzelelement.
   *
   * **Hier und nicht in der Chatansicht**, weil dieselben Variablen auch den
   * Netzstreifen und die Vorschau in den Einstellungen tragen. Und hier statt
   * im Stylesheet, weil sie aus der Datenbank kommen.
   *
   * `null` heißt: wieder wegnehmen. Wer die Farbe zurücksetzt, soll die des
   * Erscheinungsbilds zurückbekommen — auch die dunkle, und ohne Neuladen.
   */
  const werte = useSettings().data?.settings
  useEffect(() => {
    const wurzel = document.documentElement
    const variablen = blasenVariablen({
      chat_own_color: werte?.chat_own_color ?? '',
      chat_other_color: werte?.chat_other_color ?? '',
    })
    for (const [name, wert] of Object.entries(variablen)) {
      if (wert === null) wurzel.style.removeProperty(name)
      else wurzel.style.setProperty(name, wert)
    }
  }, [werte?.chat_own_color, werte?.chat_other_color])

  /**
   * Und die Farbe der Knöpfe — dieselbe Stelle, dieselbe Regel.
   *
   * Sie hängt hier und nicht an einer Seite, weil sie überall gilt: der Knopf
   * „Freigeben" auf der Karte, „GPX importieren" auf der Infoseite, der
   * Sendeknopf im Chat. Was nicht gewählt ist, wird **weggenommen** statt
   * gesetzt — nur so gilt wieder, was das Erscheinungsbild vorgibt, und die
   * dunkle Darstellung behält ihren eigenen Ton.
   */
  const akzent = werte?.accent_color
  useEffect(() => {
    const wurzel = document.documentElement
    for (const [name, wert] of Object.entries(knopfVariablen(akzent ?? ''))) {
      if (wert === null) wurzel.style.removeProperty(name)
      else wurzel.style.setProperty(name, wert)
    }
  }, [akzent])

  /**
   * Und die Fläche der Kärtchen ohne Bild — ebenfalls am Wurzelelement.
   *
   * Eingestellt wird sie **auf der Karte**, weil man sie dort sieht. Gelten
   * muss sie aber auch für die Kärtchen im Chat, und die liegen außerhalb der
   * Karte. Deshalb hier noch einmal, aus dem gespeicherten Wert; auf der Karte
   * selbst gewinnt währenddessen der Regler, weil er sie am Kartenrahmen setzt.
   */
  const kaertchen = werte?.card_color
  useEffect(() => {
    const eintrag = NADELFARBEN.find((e) => e.key === 'card_color')!
    document.documentElement.style.setProperty(eintrag.variable, kaertchen ?? eintrag.vorgabe)
  }, [kaertchen])

  // Wo man war, wird hier gemerkt und nicht in den Seiten selbst: hier kommt
  // jeder Weg vorbei, und `merkeOrt` nimmt ohnehin nur an, was zählt.
  //
  // **Die Übersicht ganz zu Beginn zählt nicht mit.** Sie ist keine
  // Entscheidung, sondern die Tür: mit ihr macht die Anwendung auf, wenn nicht
  // gerade eine Adresse mitgegeben wurde. Sie mitzuschreiben hieße, die
  // Erinnerung mit sich selbst zu überschreiben.
  //
  // Das ist nicht theoretisch. Die `StandWache` lädt beim ersten Start nach
  // einem Upload einmal neu — und auf diesem verworfenen ersten Durchgang
  // stünde sonst schon „Übersicht" im Speicher. Genau so kam auf dem iPhone
  // die Liste, obwohl zuletzt ein Chat offen war. Ein Chat als erster Weg
  // wird dagegen mitgeschrieben: den hat jemand mitgebracht.
  const ersterWeg = useRef(true)
  useEffect(() => {
    const erster = ersterWeg.current
    ersterWeg.current = false
    if (erster && location.pathname === '/') return
    merkeOrt(location.pathname)
  }, [location.pathname])

  return (
    <div className="shell" data-pane={pane}>
      <aside className="sidebar">
        <ChatList />
      </aside>
      <main className="main">
        <Outlet />
      </main>
      <RueckfrageHost />
    </div>
  )
}

